<div class="clinic-now-list w-full h-full">
    <!-- Header with search, visit count and refresh button -->
    <div class="flex items-center justify-between">
        <!-- Search Input -->
        <div class="flex-1 max-w-md">
            <mat-form-field appearance="outline" class="w-full">
                <mat-label>Search visits...</mat-label>
                <input matInput 
                       [formControl]="searchControl"
                       placeholder="Subject ID, visit name, or study number">
                <mat-icon matPrefix>search</mat-icon>
                @if (searchControl.value) {
                    <button matSuffix 
                            mat-icon-button 
                            (click)="searchControl.setValue('')"
                            title="Clear search">
                        <mat-icon>clear</mat-icon>
                    </button>
                }
            </mat-form-field>
        </div>

        <!-- Visit count and refresh -->
        <div class="flex items-center gap-4 ml-4">
            @if (totalVisits() > 0) {
                <div class="text-sm text-gray-600 dark:text-gray-400">
                    {{ totalVisits() }} visits
                    @if (searchQuery()) {
                        found
                    }
                </div>
            } @else {
                <div></div>
            }
            <button 
                mat-icon-button 
                (click)="refresh()" 
                [disabled]="loading()"
                class="text-gray-600 hover:text-gray-900"
                title="Refresh">
                <mat-icon>refresh</mat-icon>
            </button>
        </div>
    </div>

    <!-- Loading State -->
    @if (loading()) {
        <div class="flex items-center justify-center py-12">
            <mat-spinner diameter="40"></mat-spinner>
            <span class="ml-3 text-gray-600">Loading clinic visits...</span>
        </div>
    }

    <!-- Error State -->
    @else if (error()) {
        <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div class="flex items-center">
                <mat-icon class="text-red-500 mr-2">error</mat-icon>
                <div>
                    <h4 class="text-red-800 font-medium">Error Loading Visits</h4>
                    <p class="text-red-600 text-sm mt-1">{{ error() }}</p>
                </div>
                <button mat-button color="primary" (click)="refresh()" class="ml-auto">
                    Try Again
                </button>
            </div>
        </div>
    }

    <!-- Data Table -->
    @else if (hasVisits()) {
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            
            <!-- Card Layout -->
            <div class="space-y-3 p-4">
                @for (visit of paginatedVisits(); track trackByFn($index, visit)) {
                    <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow p-3">
                        
                        <!-- Visit Card Content -->
                        <div [ngClass]="{
                            'flex flex-col space-y-3': isInSidePanel,
                            'flex flex-col space-y-3 lg:flex-row lg:items-center lg:justify-between lg:space-y-0': !isInSidePanel
                        }">
                            
                            <!-- Visit Header and Details (used by all layouts) -->
                            <div [ngClass]="{
                                'flex items-center space-x-3': true,
                                'flex-1': !isInSidePanel
                            }">
                                <!-- Visit Badge -->
                                <div [ngClass]="{
                                    'w-8 h-8': isInSidePanel,
                                    'w-10 h-10': !isInSidePanel,
                                    'rounded-full bg-sky-100 dark:bg-sky-900 flex items-center justify-center flex-shrink-0': true
                                }">
                                    <span [ngClass]="{
                                        'text-xs': isInSidePanel,
                                        'text-sm': !isInSidePanel,
                                        'font-semibold text-sky-800 dark:text-sky-200': true
                                    }">
                                        {{ getParticipantAvatar(visit.subject) }}
                                    </span>
                                </div>
                                
                                <!-- Visit Details -->
                                <div class="flex-1 min-w-0">
                                    <div class="flex items-center gap-2 flex-wrap">
                                        <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 flex-shrink-0">
                                            {{ visit.subject.subject_id }}
                                        </span>
                                        <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 flex-shrink-0">
                                            {{ visit.study_number }}
                                        </span>
                                        <h4 [ngClass]="{
                                            'text-sm font-medium text-gray-900 dark:text-white': true,
                                            'truncate': isInSidePanel
                                        }"
                                        [matTooltip]="visit.visit_name">
                                            {{ visit.visit_name }}
                                        </h4>
                                    </div>
                                    
                                    <!-- Assigned Personnel by Signature Workflow (always show on dashboard, hide on side panel in stacked layout) -->
                                    @if (!isInSidePanel) {
                                        <div class="mt-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 md:max-w-[75%]">
                                            @if (visit.assigned_personnel_steps && visit.assigned_personnel_steps.length > 0) {
                                                @for (step of visit.assigned_personnel_steps; track step.label) {
                                                    <div class="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                                        <mat-icon class="mr-1 text-sm">group</mat-icon>
                                                        <span [title]="step.label + ': ' + step.names.join(', ')">
                                                            <span class="font-medium">{{ step.label }}:</span> {{ step.names.join(', ') }}
                                                        </span>
                                                    </div>
                                                }
                                            }
                                        </div>
                                        
                                        <!-- Time status (always show on dashboard) -->
                                        @if (visit.arrived || visit.departed || visit.appointment_start_date) {
                                            <div class="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400">
                                                <mat-icon class="mr-1 text-sm">schedule</mat-icon>
                                                <span>
                                                    @if (visit.arrived && visit.departed) {
                                                        <span class="font-medium">Arrived: {{ visit.arrived | date:'h:mm a' }}</span>
                                                        <span class="mx-2">•</span>
                                                        <span class="font-medium">Departed: {{ visit.departed | date:'h:mm a' }}</span>
                                                    } @else if (visit.arrived) {
                                                        <span class="font-medium">Arrived: {{ visit.arrived | date:'h:mm a' }}</span>
                                                    } @else if (visit.appointment_start_date) {
                                                        <span class="font-medium">Scheduled: {{ visit.appointment_start_date | date:'h:mm a' }}</span>
                                                    }
                                                </span>
                                            </div>
                                        }
                                    }
                                </div>
                                
                                <!-- Status indicator (for side panel and narrow dashboard layouts) -->
                                <div [ngClass]="{
                                    'w-3 h-3 rounded-full flex-shrink-0': true,
                                    'lg:hidden': !isInSidePanel,
                                    'bg-gray-400': !visit.arrived && !visit.departed,
                                    'bg-yellow-400': visit.arrived && !visit.departed,
                                    'bg-green-400': visit.departed
                                }" 
                                [matTooltip]="visit.arrived ? (visit.departed ? 'Completed' : 'In Progress') : 'Scheduled'">
                                </div>
                            </div>
                            
                            <!-- Assigned Personnel by Signature Workflow (for side panel only) -->
                            @if (isInSidePanel) {
                                <div class="flex flex-col gap-0.5">
                                    @if (visit.assigned_personnel_steps && visit.assigned_personnel_steps.length > 0) {
                                        @for (step of visit.assigned_personnel_steps; track step.label) {
                                            <div class="flex items-center text-xs text-gray-500 dark:text-gray-400">
                                                <mat-icon class="mr-1 text-xs">group</mat-icon>
                                                <span [title]="step.label + ': ' + step.names.join(', ')">
                                                    <span class="font-medium">{{ step.label }}:</span> {{ step.names.join(', ') }}
                                                </span>
                                            </div>
                                        }
                                    }
                                </div>
                            }
                            
                            <!-- Time status (for side panel only) -->
                            @if (isInSidePanel && (visit.arrived || visit.departed || visit.appointment_start_date)) {
                                <div class="flex items-center text-xs text-gray-500 dark:text-gray-400">
                                    <mat-icon class="mr-1 text-xs">schedule</mat-icon>
                                    <span>
                                        @if (visit.arrived && visit.departed) {
                                            <span class="font-medium">Arrived: {{ visit.arrived | date:'h:mm a' }}</span>
                                            <span class="mx-2">•</span>
                                            <span class="font-medium">Departed: {{ visit.departed | date:'h:mm a' }}</span>
                                        } @else if (visit.arrived) {
                                            <span class="font-medium">Arrived: {{ visit.arrived | date:'h:mm a' }}</span>
                                        } @else if (visit.appointment_start_date) {
                                            <span class="font-medium">Scheduled: {{ visit.appointment_start_date | date:'h:mm a' }}</span>
                                        }
                                    </span>
                                </div>
                            }
                            
                            <!-- Action buttons for side panel (text buttons in new row) -->
                            @if (isInSidePanel) {
                                <div class="flex items-center justify-start gap-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                                    <!-- View details button -->
                                    <button mat-button 
                                            size="small"
                                            (click)="viewDetails(visit)"
                                            class="text-xs min-w-0">
                                        <mat-icon class="mr-1 text-xs">visibility</mat-icon>
                                        View Details
                                    </button>
                                    
                                    <!-- Check-in button -->
                                    @if (!visit.arrived) {
                                        <button mat-button 
                                                size="small"
                                                color="primary"
                                                (click)="checkIn(visit)"
                                                [disabled]="isButtonsDisabled()"
                                                class="text-xs min-w-0">
                                            <mat-icon class="mr-1 text-xs">login</mat-icon>
                                            Check In
                                        </button>
                                    }
                                    
                                    <!-- Check-out button -->
                                    @if (visit.arrived && !visit.departed) {
                                        <button mat-button 
                                                size="small"
                                                color="accent"
                                                (click)="checkOut(visit)"
                                                [disabled]="isButtonsDisabled()"
                                                class="text-xs min-w-0">
                                            <mat-icon class="mr-1 text-xs">logout</mat-icon>
                                            Check Out
                                        </button>
                                    }
                                    
                                    <!-- Completed indicator with navigation -->
                                    @if (visit.departed) {
                                        <button mat-button
                                                size="small"
                                                color="primary"
                                                (click)="openParticipant(visit)"
                                                class="text-xs min-w-0">
                                            <mat-icon class="mr-1 text-xs">check_circle</mat-icon>
                                            View Participant
                                        </button>
                                    }
                                </div>
                            }
                            
                            <!-- Action buttons for dashboard (responsive: text on narrow, icons on wide) -->
                            @if (!isInSidePanel) {
                                <!-- Text buttons for narrow dashboard -->
                                <div class="flex items-center justify-start gap-2 pt-2 border-t border-gray-100 dark:border-gray-700 lg:hidden">
                                    <!-- View details button -->
                                    <button mat-button 
                                            size="small"
                                            (click)="viewDetails(visit)"
                                            class="text-xs min-w-0">
                                        <mat-icon class="mr-1 text-sm">visibility</mat-icon>
                                        View Details
                                    </button>
                                    
                                    <!-- Check-in button -->
                                    @if (!visit.arrived) {
                                        <button mat-button 
                                                size="small"
                                                color="primary"
                                                (click)="checkIn(visit)"
                                                [disabled]="isButtonsDisabled()"
                                                class="text-xs min-w-0">
                                            <mat-icon class="mr-1 text-sm">login</mat-icon>
                                            Check In
                                        </button>
                                    }
                                    
                                    <!-- Check-out button -->
                                    @if (visit.arrived && !visit.departed) {
                                        <button mat-button 
                                                size="small"
                                                color="accent"
                                                (click)="checkOut(visit)"
                                                [disabled]="isButtonsDisabled()"
                                                class="text-xs min-w-0">
                                            <mat-icon class="mr-1 text-sm">logout</mat-icon>
                                            Check Out
                                        </button>
                                    }
                                    
                                    <!-- Completed indicator with navigation -->
                                    @if (visit.departed) {
                                        <button mat-button
                                                size="small"
                                                color="primary"
                                                (click)="openParticipant(visit)"
                                                class="text-xs min-w-0">
                                            <mat-icon class="mr-1 text-sm">check_circle</mat-icon>
                                            View Participant
                                        </button>
                                    }
                                </div>
                                
                                <!-- Icon buttons for wide dashboard -->
                                <div class="hidden lg:flex items-center space-x-2 clinic-actions">
                                    <!-- View details button -->
                                    <button mat-icon-button 
                                            (click)="viewDetails(visit)"
                                            matTooltip="View Visit Details"
                                            class="text-gray-600 hover:text-blue-600">
                                        <mat-icon>visibility</mat-icon>
                                    </button>
                                    
                                    <!-- Check-in button -->
                                    @if (!visit.arrived) {
                                        <button mat-icon-button 
                                                (click)="checkIn(visit)"
                                                [disabled]="isButtonsDisabled()"
                                                matTooltip="Check In Participant"
                                                [class]="isButtonsDisabled() ? 'text-gray-400 cursor-not-allowed' : 'text-gray-600 hover:text-green-600'">
                                            <mat-icon>login</mat-icon>
                                        </button>
                                    }
                                    
                                    <!-- Check-out button -->
                                    @if (visit.arrived && !visit.departed) {
                                        <button mat-icon-button 
                                                (click)="checkOut(visit)"
                                                [disabled]="isButtonsDisabled()"
                                                matTooltip="Check Out Participant"
                                                [class]="isButtonsDisabled() ? 'text-gray-400 cursor-not-allowed' : 'text-gray-600 hover:text-orange-600'">
                                            <mat-icon>logout</mat-icon>
                                        </button>
                                    }
                                    
                                    <!-- Completed indicator with navigation -->
                                    @if (visit.departed) {
                                        <button mat-icon-button
                                                (click)="openParticipant(visit)"
                                                matTooltip="View Participant Details"
                                                class="text-green-600 hover:text-green-700">
                                            <mat-icon>check_circle</mat-icon>
                                        </button>
                                    }
                                    
                                    <!-- Status indicator -->
                                    <div class="w-3 h-3 rounded-full" 
                                         [ngClass]="{
                                             'bg-gray-400': !visit.arrived && !visit.departed,
                                             'bg-yellow-400': visit.arrived && !visit.departed,
                                             'bg-green-400': visit.departed
                                         }"
                                         [matTooltip]="visit.arrived ? (visit.departed ? 'Completed' : 'In Progress') : 'Scheduled'">
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                }
            </div>
            
            <!-- Pagination -->
            <div class="border-t border-gray-200 dark:border-gray-700">
                <mat-paginator
                    [length]="totalVisits()"
                    [pageSize]="page().size"
                    [pageIndex]="page().pageNumber"
                    [pageSizeOptions]="isInSidePanel ? [5, 10, 25] : [5, 10, 25, 50]"
                    showFirstLastButtons
                    (page)="onPaginatorChange($event)"
                    class="border-0">
                </mat-paginator>
            </div>
        </div>

        <!-- Table Footer with Summary -->
        <div class="mt-4 flex items-center justify-between text-sm text-gray-600">
            <div>
                Showing {{ currentPageVisits() }} of {{ totalVisits() }} visits
                @if (searchQuery()) {
                    for "{{ searchQuery() }}"
                }
                @if (visitsWithStatus().inProgress > 0 || visitsWithStatus().completed > 0) {
                    • {{ visitsWithStatus().inProgress }} in progress
                    • {{ visitsWithStatus().completed }} completed
                }
            </div>
        </div>
    }

    <!-- Empty State -->
    @else {
        <div class="text-center py-12">
            @if (searchQuery()) {
                <mat-icon class="text-gray-400 mb-4" style="font-size: 48px;">search_off</mat-icon>
                <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                    No visits found
                </h3>
                <p class="text-gray-600 dark:text-gray-400 mb-4">
                    No clinic visits match your search for "{{ searchQuery() }}".
                </p>
                <button mat-button color="primary" (click)="searchControl.setValue('')">
                    Clear search
                </button>
            } @else {
                <mat-icon class="text-gray-400 mb-4" style="font-size: 48px;">event_available</mat-icon>
                <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                    No Clinic Visits Today
                </h3>
            }
        </div>
    }
</div>

